import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

interface Form {
  id: string;
  name: string;
  description: string;
  category: string;
  isActive: boolean;
  customerPortalSettings?: {
    isAvailableOnPortal: boolean;
    allowedCustomers?: string[];
    allowAllCustomers?: boolean;
  };
  publicSettings?: {
    isPublic: boolean;
    publicId: string;
    allowAnonymous: boolean;
  };
  createdAt: Date;
}

const AvailableForms: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    if (user?.id) {
      loadPublishedForms();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadPublishedForms = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Query forms by organizationId
      const q = query(
        collection(db, 'formTemplates'),
        where('organizationId', '==', user.organizationId)
      );
      
      const snapshot = await getDocs(q);
      
      const allForms = snapshot.docs
        .map(doc => {
          const data = doc.data();
          // Remove the 'id' field from data to avoid overwriting doc.id
          const { id: _, ...dataWithoutId } = data as any;
          return {
            id: doc.id, // Use Firestore document ID
            ...dataWithoutId,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date())
          } as Form;
        })
        .filter(form => form.name && form.isActive); // Filter by name and isActive
      
      // Filter forms based on customer portal settings
      const filteredForms = allForms.filter(form => {
        // If no customer portal settings, don't show
        if (!form.customerPortalSettings) return false;
        
        // If not available on portal, don't show
        if (!form.customerPortalSettings.isAvailableOnPortal) return false;
        
        // If allowed for all customers, show
        if (form.customerPortalSettings.allowAllCustomers) return true;
        
        // If specific customers allowed, check if current customer is in the list
        if (form.customerPortalSettings.allowedCustomers && user.customerId) {
          return form.customerPortalSettings.allowedCustomers.includes(user.customerId);
        }
        
        return false;
      });
      
      setForms(filteredForms);
    } catch (error) {
      console.error('Error loading forms:', error);
      toast.error(t('common.errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFormClick = (formId: string) => {
    navigate(`/customer-portal/forms/${formId}/fill`);
  };

  // Collect all unique categories
  const categories = Array.from(new Set(forms.map(f => f.category).filter(Boolean))).sort();

  // Filter forms by category
  const filteredForms = categoryFilter === 'all' 
    ? forms 
    : forms.filter(f => f.category === categoryFilter);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('customerPortal.submissions.noForms')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('customerPortal.submissions.noFormsDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and View Mode Toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Típus:</span>
            <button
              onClick={() => setCategoryFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                categoryFilter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Összes ({forms.length})
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setCategoryFilter(category)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  categoryFilter === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category} ({forms.filter(f => f.category === category).length})
              </button>
            ))}
          </div>
        )}
        
        {/* View Mode Toggle */}
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1 bg-gray-50 dark:bg-gray-800">
          <button
            onClick={() => setViewMode('grid')}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'grid'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4 mr-1.5" />
            Kártya
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <ListBulletIcon className="h-4 w-4 mr-1.5" />
            Lista
          </button>
        </div>
      </div>

      {/* No results message */}
      {filteredForms.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            Nincs találat
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Nincs űrlap ebben a kategóriában
          </p>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && filteredForms.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredForms.map((form) => (
            <div
              key={form.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleFormClick(form.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {form.name}
                    </h3>
                  </div>
                  
                  {form.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {form.description}
                    </p>
                  )}
                  
                  {form.category && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      {form.category}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('customerPortal.submissions.clickToFill') || 'Click to fill'}
                </span>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && filteredForms.length > 0 && (
        <div className="space-y-2">
          {filteredForms.map((form) => (
            <div
              key={form.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleFormClick(form.id)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                  <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 truncate">
                      {form.name}
                    </h3>
                    {form.description && (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate hidden sm:block">
                        {form.description}
                      </p>
                    )}
                  </div>
                  {form.category && (
                    <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 flex-shrink-0">
                      {form.category}
                    </span>
                  )}
                </div>
                <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              </div>
              
              {/* Mobile: Show category below on small screens */}
              {form.category && (
                <div className="sm:hidden mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                    {form.category}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvailableForms;
